import React, { useCallback, useState } from 'react';

import { StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';
import { InfoLabel } from '@eika/label';

import { TransactionForm, TransactionSubmitWrapper } from '../../components/TransactionForm';
import { SelectList } from '../../components/shared/SelectList';
import createAvtalegiroProposalRequest from '../../components/transaction/AvtaleGiro/form/createAvtalegiroProposalRequest';
import useFormContext from '../../components/transaction/AvtaleGiro/form/useFormContext';
import AvtaleGiroResultInfo from '../../components/transaction/AvtaleGiroResultInfo';
import AvtaleGiroRetry from '../../components/transaction/AvtaleGiroRetry';
import { SelectedReceiver } from '../../components/transaction/SelectedReceiver';
import { useConfig } from '../../defaultConfig';
import type { Account, AvtaleGiroProposalId, ApprovalStatus } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import AvtaleGiroEditableDetails from './AvtaleGiroEditableDetails';
import AvtaleGiroProposalFooter from './AvtaleGiroProposalFooter';
import DeleteAvtaleGiroProposal from './DeleteAvtaleGiroProposal';

const hasSigningError = (paymentStatus?: ApprovalStatus) => {
    return paymentStatus === 'REJECTED' || paymentStatus === 'UNKNOWN';
};

interface Props {
    creditorName: string;
    accounts: Account[];
    accountState: { isLoading: boolean; refetch: () => void };
    receiver?: SelectedReceiver;
    onClose: () => void;
    proposalId: AvtaleGiroProposalId;
    onDeleted: () => void;
}

const AvtaleGiroProposalsForm: React.FC<Props> = ({
    accounts,
    proposalId,
    creditorName,
    accountState,
    receiver,
    onClose,
    onDeleted,
}) => {
    const translated = useTranslation();
    const { state, checkFormValidity } = useFormContext();
    const {
        featureToggles: { disableAvtaleGiroChanges },
    } = useConfig();
    const {
        signingStatus,
        signingError,
        signingOperationPending,
        handleCancelSigningFlow,
        startSigning,
        showPaymentRetry,
    } = useSigningContext(createAvtalegiroProposalRequest(proposalId));

    const [showDeleteProposal, setShowDeleteProposal] = useState(false);

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [startSigning, checkFormValidity, state],
    );

    if (disableAvtaleGiroChanges) {
        return (
            <InfoCard className="create-avtalegiro-info-card" skipBackground>
                <Icon className="create-avtalegiro-info-card__icon" type={IconTypes.UTROPSTEGN_36} />
                <InfoCard.TitleH2>{translated.disableAvtaleGiroChanges.creationUnavailableTitle}</InfoCard.TitleH2>
                <p>{translated.disableAvtaleGiroChanges.creationUnavailableDescription}</p>
                <StandardButton className="create-avtalegiro-info-card__btn" onClick={onClose}>
                    {translated.ok}
                </StandardButton>
            </InfoCard>
        );
    }

    if (showPaymentRetry) {
        return <AvtaleGiroRetry onClose={onClose} />;
    }

    if (showDeleteProposal) {
        return (
            <DeleteAvtaleGiroProposal
                proposalId={proposalId}
                onCancel={() => setShowDeleteProposal(false)}
                onDelete={onDeleted}
                showDialogue
            />
        );
    }

    if (signingError || signingStatus === 'COMPLETED' || hasSigningError(signingStatus)) {
        return (
            <AvtaleGiroResultInfo
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
                onRetryManually={() => handleCancelSigningFlow()}
            />
        );
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <div className="avtalegiro__proposals-container">
                <div className="avtalegiro__proposals-label-container">
                    <InfoLabel size="small" text={translated.avtaleGiroProposals.new} />
                </div>
                <div>
                    <h2 className="avtalegiro__proposals-title">{creditorName}</h2>
                </div>
                <SelectList>
                    <AvtaleGiroEditableDetails
                        className="avtalegiro__proposals-list"
                        accounts={accounts}
                        accountState={accountState}
                        receiver={receiver}
                    />
                    <TransactionForm onSubmit={onSubmit}>
                        <TransactionSubmitWrapper className="avtalegiro__footer">
                            <AvtaleGiroProposalFooter
                                paymentOperationPending={signingOperationPending}
                                deleteProposal={() => setShowDeleteProposal(true)}
                            />
                        </TransactionSubmitWrapper>
                    </TransactionForm>
                </SelectList>
            </div>
        </SigningContextFlow>
    );
};

export default AvtaleGiroProposalsForm;
